import React, { useState, useEffect } from 'react';

// Pop-up form component
const ServiceFormPopup = ({ serviceName, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    number: '',
    email: '',
    message: '',
  });
  const [submissionStatus, setSubmissionStatus] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmissionStatus('Submitting...');
    // Simulate API call
    setTimeout(() => {
      console.log('Form Submitted for Service:', serviceName, formData);
      setSubmissionStatus(`Thank you, ${formData.name}! Your request has been submitted.`);
      // Clear form after submission
      setFormData({ name: '', number: '', email: '', message: '' });
      // Optionally close the popup after a delay
      setTimeout(onClose, 2000);
    }, 1000);
  };

  const formFieldStyle = {
    backgroundColor: '#ffffff', // Changed to white
    border: '1px solid #e2e8f0', // Changed to light gray border
    color: '#1f2937', // Changed to dark text color
    padding: '10px',
    borderRadius: '5px',
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)', // Kept dark for overlay effect
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#f8fafc', // Light background for the popup
        color: '#1f2937', // Dark text color
        padding: '30px',
        borderRadius: '10px',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
        position: 'relative',
      }}>
        <button onClick={onClose} style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          backgroundColor: 'transparent',
          border: 'none',
          color: '#334155', // Dark close button
          fontSize: '24px',
          cursor: 'pointer',
        }}>
          &times;
        </button>
        <h2 style={{ color: '#d4b68f', marginBottom: '20px', textAlign: 'center', fontFamily: 'serif', fontWeight: 'bold' }}>
          Request for: <br /> {serviceName}
        </h2>
        {submissionStatus ? (
          <p style={{ textAlign: 'center', color: '#ff8c00', fontSize: '1.2rem' }}>{submissionStatus}</p>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <input
              type="text"
              name="name"
              placeholder="Name"
              value={formData.name}
              onChange={handleChange}
              required
              style={formFieldStyle}
            />
            <input
              type="tel"
              name="number"
              placeholder="Phone Number"
              value={formData.number}
              onChange={handleChange}
              required
              style={formFieldStyle}
            />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              required
              style={formFieldStyle}
            />
            <textarea
              name="message"
              placeholder="Message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="4"
              style={{ ...formFieldStyle, resize: 'vertical' }}
            />
            <button type="submit" style={{
              backgroundColor: '#d4b68f',
              color: '#1e293b',
              border: 'none',
              padding: '12px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'background-color 0.2s',
            }}>
              Submit Request
            </button>
          </form>
        )}
      </div>
    </div>
  );
};


const ServicesPage = () => {
  const [inView, setInView] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedService, setSelectedService] = useState('');

  const handleServiceClick = (serviceTitle) => {
    setSelectedService(serviceTitle);
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedService('');
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const servicesGrid = document.querySelector('.ServicesPages-grid');
    if (servicesGrid) {
      observer.observe(servicesGrid);
    }

    return () => {
      if (servicesGrid) {
        observer.unobserve(servicesGrid);
      }
    };
  }, []);

  const services = [
    {
      title: 'Web Development',
      description: 'We craft stunning, responsive, and high-performing websites tailored to your brand. From simple portfolios to complex e-commerce platforms, we build digital experiences that engage users and drive results.',
      icon: '🌐',
      id: 'web-dev'
    },
    {
      title: 'Custom Software',
      description: 'Our team develops bespoke software solutions to meet your unique business challenges. We build scalable, secure, and efficient applications from the ground up, designed to streamline your operations.',
      icon: '🖥️',
      id: 'software-dev',
      highlighted: true
    },
    {
      title: 'IT Support & Cybersecurity',
      description: 'Protect your business with our robust cybersecurity solutions and reliable IT support. We provide proactive monitoring, threat detection, and swift technical assistance to keep your systems secure and running smoothly.',
      icon: '🛡️',
      id: 'cybersecurity'
    },
    {
      title: 'Cloud & DevOps Solutions',
      description: 'Harness the power of the cloud with our expert services. We offer cloud migration, infrastructure management, and DevOps automation to enhance collaboration, accelerate deployment, and optimize your IT infrastructure.',
      icon: '☁️',
      id: 'cloud-devops'
    },
    {
      title: 'IT Consulting',
      description: 'Our consultants provide strategic guidance to help you navigate the complexities of technology. We analyze your business needs, recommend optimal solutions, and create a clear roadmap for your digital transformation.',
      icon: '🤝',
      id: 'it-consulting'
    },
    {
      title: 'Custom Services?',
      description: 'Have a unique project in mind? Let\'s discuss your specific needs and create a tailored solution that fits your vision. Contact us to explore how we can help.',
      icon: '✉️',
      id: 'custom-services',
      isCustom: true
    },
  ];

  const ServiceCard = ({ service, index }) => (
    <div
      key={index}
      onClick={() => handleServiceClick(service.title)}
      style={{
        backgroundColor: service.highlighted ? '#fefce8' : '#ffffff', // Changed to lighter shades
        border: service.highlighted ? '1px solid #d4b68f' : '1px solid #e2e8f0', // Lighter border
        boxShadow: service.highlighted ? '0 0 15px rgba(212, 182, 143, 0.3)' : '0 4px 6px rgba(0, 0, 0, 0.05)', // Added a subtle shadow
        borderRadius: '10px',
        padding: '25px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        textAlign: 'left',
        color: '#334155', // Darker text color
        transition: 'transform 0.3s ease, box-shadow 0.3s ease, opacity 0.8s ease-out',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${index * 0.15}s`,
        cursor: 'pointer',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = 'translateY(-10px)';
        e.currentTarget.style.boxShadow = '0 15px 30px rgba(0, 0, 0, 0.1)'; // Lighter shadow
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = service.highlighted ? '0 0 15px rgba(212, 182, 143, 0.3)' : '0 4px 6px rgba(0, 0, 0, 0.05)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, margin: 0, color: '#1f2937' }}>{service.title}</h2>
        <div style={{
          fontSize: '2em',
          backgroundColor: '#f1f5f9', // Lighter background for the icon
          borderRadius: '8px',
          padding: '10px'
        }}>
          {service.icon}
        </div>
      </div>
      <div>
        <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.6, margin: 0 }}>
          {service.description}
        </p>
      </div>
    </div>
  );

  const CustomServiceCard = ({ service, index }) => (
    <div
      onClick={() => handleServiceClick(service.title)}
      style={{
        backgroundColor: '#ffffff', // Changed to white
        border: '1px solid #e2e8f0', // Light border
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        borderRadius: '10px',
        padding: '25px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        justifyContent: 'center',
        color: '#334155', // Dark text color
        transition: 'transform 0.3s ease, box-shadow 0.3s ease, opacity 0.8s ease-out',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${index * 0.15}s`,
        cursor: 'pointer',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = 'translateY(-10px)';
        e.currentTarget.style.boxShadow = '0 15px 30px rgba(0, 0, 0, 0.1)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
      }}
    >
      <div style={{ padding: '10px', backgroundColor: '#f1f5f9', borderRadius: '8px', marginBottom: '15px' }}>
        <div style={{ fontSize: '2em' }}>{service.icon}</div>
      </div>
      <h2 style={{ fontSize: '24px', marginBottom: '10px', color: '#1f2937', fontWeight: 600 }}>{service.title}</h2>
      <p style={{ fontSize: '14px', marginBottom: '20px', color: '#475569' }}>{service.description}</p>
      <button
        style={{
          backgroundColor: '#d4b68f',
          color: '#1e293b',
          border: 'none',
          padding: '12px 24px',
          borderRadius: '5px',
          cursor: 'pointer',
          fontFamily: 'sans-serif',
          fontWeight: '600',
          transition: 'background-color 0.2s ease, transform 0.2s ease'
        }}
        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f59e0b'}
        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#d4b68f'}
      >
        Let's Discuss
      </button>
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '4rem 0',
        background: '#f0f4f8', // Changed to off-white
        position: 'relative',
        fontFamily: 'sans-serif',
        color: '#334155', // Dark text color
        overflow: 'hidden',
      }}
    >
      <svg
        style={{
          position: 'absolute',
          top: -1,
          left: 0,
          width: '100%',
          zIndex: 1,
          transform: 'rotate(180deg)',
          pointerEvents: 'none',
        }}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <path
          fill="#1e293b" // Changed to a dark color for visibility
          fillOpacity="1"
          d="M0,288L60,266.7C120,245,240,203,360,208C480,213,600,267,720,282.7C840,299,960,277,1080,272C1200,267,1320,277,1380,282.7L1440,288L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"
        ></path>
      </svg>
      <div
        className="services-content-wrapper"
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          position: 'relative',
          zIndex: 2,
          padding: '4rem 2rem',
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            marginBottom: '3rem',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, textAlign: 'left' }}>
            <h3
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#d4b68f',
                letterSpacing: '1px',
                marginBottom: '0.5rem',
                textTransform: 'uppercase'
              }}
            >
              Our Services
            </h3>
            <h1
              style={{
                color: '#1f2937', // Dark heading color
                backgroundClip: 'unset', // Removed as gradient is no longer needed
                backgroundImage: 'none',
                fontSize: '2.5rem',
                fontWeight: 700,
                lineHeight: 1.2,
                margin: 0,
                fontFamily: 'serif',
              }}
            >
              Expert IT Solutions <br /> for Your Business
            </h1>
          </div>
          <p
            style={{
              fontSize: '1rem',
              fontWeight: 400,
              color: '#475569', // Dark paragraph color
              lineHeight: 1.6,
              marginLeft: '2rem',
              flex: 1
            }}
          >
            We deliver top-tier IT solutions tailored to your business needs—combining innovation, reliability, and expert support to drive digital success.
          </p>
        </div>

        <div
          className="ServicesPages-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            width: '100%',
            backgroundColor: '#f1f5f9', // Light background for the grid container
            padding: '2rem',
            borderRadius: '10px',
            boxShadow: '0 10px 20px rgba(0,0,0,0.1)', // Lighter shadow
          }}
        >
          {services.map((service, index) =>
            service.isCustom ? (
              <CustomServiceCard key={index} service={service} index={index} />
            ) : (
              <ServiceCard key={index} service={service} index={index} />
            )
          )}
        </div>
      </div>

      {showPopup && (
        <ServiceFormPopup
          serviceName={selectedService}
          onClose={closePopup}
        />
      )}
      <style>{`
        .ServicesPages-grid div:hover {
          transform: translateY(-10px) !important;
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1) !important; // Adjusted for light background
        }

        @media (max-width: 992px) {
          .services-content-wrapper {
            padding: 2rem 1rem !important;
          }
          .services-content-wrapper > div:first-of-type {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .services-content-wrapper > div:first-of-type p {
            margin-left: 0 !important;
            margin-top: 1.5rem !important;
          }
        }
        @media (max-width: 768px) {
          .ServicesPages-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
            padding: 1rem !important;
          }
          .ServicesPages-grid div {
            padding: 1.5rem !important;
          }
        }
        @media (max-width: 480px) {
          .services-content-wrapper h1 {
            font-size: 1.8rem !important;
          }
          .services-content-wrapper h3, .services-content-wrapper p {
             font-size: 0.8rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ServicesPage;